import { useCallback, useContext, useMemo, useState } from 'react';
import { CheckoutContext } from '@/context/CheckoutContext';
import { InputTaxComponent } from '../InputTaxComponent/InputTaxComponent';
import { PayButton } from '../PaymentInputs/PayButton';
import { BrandedPayCTA } from '../PaymentInputs/BrandedPayCTA';
import { Email } from '../Common/Email';
import { Input } from '../Common/Input';
import { BlikAuthorizationScreen } from './BlikAuthorizationScreen';
import { shouldHideEmail, shouldShowLoader } from '@/utils/conditionalRendering';
import { isEmailValid } from '@/utils/email';
import { isBlikCodeValid } from '@/utils/stripe';
import { useStripeInitialize } from '@/hooks/useStripeInitialize';
import { useTranslate } from '@/hooks/useTranslate';
import { useBrandedPayCta } from '@/hooks/useBrandedPayCta';
import { PaymentMethodEnum } from '@/types/common';
import { BLIK_CODE_LENGTH } from '@/constants/payment';
import type { BlikPaymentParams } from '@/types/paymentHandlers';

export const StripeBlikPayForm = () => {
  const {
    actions: { runPayment },
    payment: { isInPaymentProcess },
    successScreen,
    customer,
    featureFlags,
    template,
  } = useContext(CheckoutContext);
  const { t } = useTranslate();

  const { stripe } = useStripeInitialize({ paymentMethod: PaymentMethodEnum.BLIK });

  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [isCustomerAddressAndZipValid, setIsCustomerAddressAndZipValid] = useState(false);

  const handleAddressAndZipValidate = useCallback((isValid: boolean) => {
    setIsCustomerAddressAndZipValid(isValid);
  }, []);

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value.replace(/\D/g, '').slice(0, BLIK_CODE_LENGTH));
  };

  const isPayButtonDisabled = useMemo(() => {
    return !stripe || !isBlikCodeValid(code) || !isCustomerAddressAndZipValid || !isEmailValid(customer?.email);
  }, [stripe, code, isCustomerAddressAndZipValid, customer?.email]);

  const handlePayment = async () => {
    await runPayment<unknown>({ code } satisfies BlikPaymentParams);
  };

  const showAuthorizationScreen = isInPaymentProcess && !successScreen?.isOpen && shouldShowLoader(featureFlags);

  // Templates like PREFERRED (where the primary CTA IS the selected wallet)
  // paint a brand-locked "Pay with BLIK" button in place of the neutral one.
  // BLIK has no Stripe ECE — we own the visual via <BrandedPayCTA>. See
  // src/hooks/useBrandedPayCta.ts + brandedPayCta.config.tsx.
  const { shouldRenderBrandedCta } = useBrandedPayCta(PaymentMethodEnum.BLIK);

  return (
    <div className="flex flex-col gap-4">
      {showAuthorizationScreen && <BlikAuthorizationScreen />}
      {!shouldHideEmail(featureFlags, customer, template.features) && <Email />}
      <InputTaxComponent onValidate={handleAddressAndZipValidate} />
      <Input
        type="text"
        inputMode="numeric"
        maxLength={BLIK_CODE_LENGTH}
        placeholder={t('blikCode')}
        value={code}
        onChange={handleCodeChange}
        onBlur={() => setCodeTouched(true)}
        onFocus={() => setCodeTouched(false)}
        error={codeTouched && !isBlikCodeValid(code) ? t('fieldErrors.invalidBlikCode') : undefined}
        className="w-full text-base"
      />
      {shouldRenderBrandedCta ? (
        <BrandedPayCTA
          paymentMethod={PaymentMethodEnum.BLIK}
          onClick={handlePayment}
          isDisabled={isPayButtonDisabled}
        />
      ) : (
        <PayButton onClick={handlePayment} isDisabled={isPayButtonDisabled} />
      )}
    </div>
  );
};
